import Interval from './Interval'
import { easing, EaseCurve, EaseType } from './easing'
import { clamp, lerp } from './math'

export function isMobile(): boolean {
	return (navigator.maxTouchPoints > 1 && innerWidth < 1024) || innerWidth < 768
}

export type ElementOffset = {
	offsetLeft: number
	offsetTop: number
}

export function offsetOfElement(element: HTMLElement, top = document.body): ElementOffset {
	let node = element
	let { offsetLeft, offsetTop } = node
	while ((node = node.parentElement) && 'offsetLeft' in node && node != top) {
		offsetLeft += node.offsetLeft
		offsetTop += node.offsetTop
	}
	return { offsetLeft, offsetTop }
}

export interface ScrollToOptions {
	callback?: () => void
	curve?: EaseCurve
	duration?: number
}

export const scrollTo = function () {
	const start = { x: 0, y: 0 }
	const end = { ...start }

	let curve = EaseCurve.Linear
	let callback = null

	const interval = new Interval({
		autostart: false
	}, (pos) => {
		const ease = easing(pos, EaseType.InOut, curve)
		const x = lerp(start.x, end.x, ease)
		const y = lerp(start.y, end.y, ease)
		window.scrollTo(x, y)

		if (pos == 1) {
			callback?.()
			document.documentElement.removeAttribute('data-disable-snap')
		}
	})

	const cancel = () => {
		if (interval.isRunning) {
			interval.stop()
			document.documentElement.removeAttribute('data-disable-snap')
		}
	}

	// Stop when the user is potentially manually scrolling.
	addEventListener('mousewheel', cancel)
	addEventListener('keydown', cancel)

	return function (x = 0, y = 0, options: ScrollToOptions = {}) {
		document.documentElement.dataset.disableSnap = 'disable'

		interval.duration = options.duration || 800
		curve = options.curve || EaseCurve.Quint
		callback = options.callback

		interval.stop()
		start.x = window.scrollX || pageXOffset || document.body.scrollLeft
		start.y = window.scrollY || pageYOffset || document.body.scrollTop
		end.x = clamp(x, 0, document.body.scrollWidth - innerWidth)
		end.y = clamp(y, 0, document.body.scrollHeight - innerHeight)
		interval.start()
	}
}()

export interface ScrollToElementOptions extends ScrollToOptions {
	offsetLeft?: number
	offsetTop?: number
}

export const scrollToElement = function (target: HTMLElement, options: ScrollToElementOptions = {}) {
	const offset = offsetOfElement(target)
	const offsetLeft = options.offsetLeft || 0
	const offsetTop = options.offsetTop || 0
	scrollTo(offset.offsetLeft + offsetLeft, offset.offsetTop + offsetTop, options)
}